import React, { useState } from "react";

/**
 * RowGroup
 * - Layout-only upgrades (polish, spacing, hierarchy, sticky row index)
 * - Single custom tooltip (no native title -> no duplicate hover)
 * - FIX: Deduplicate category messages when filtering (row/field/timeliness)
 *
 * Expected row shape (unchanged):
 * {
 *   rowIndex?: number,
 *   data: {
 *     [fieldName]: {
 *       value: any,
 *       issue?: string | { message: string },
 *       category?: string
 *     }
 *   },
 *   issues?: Array<{ category: string, title?: string, field?: string, message?: string }>,
 *   timeliness?: Array<{ scenario?: string, message?: string }>
 * }
 */

export default function RowGroup({
  rows,
  groupLabel,
  allFields,
  selectedCategories = [],
  onlyShowErroredRows = false,
}) {
  const [expanded, setExpanded] = useState(true);

  /* ----------------------- UI Helpers ----------------------- */

  // Lightweight tooltip (CSS-only). We do NOT use the native title attribute.
  const Tooltip = ({ category, message }) => (
    <div className="pointer-events-none absolute right-2 top-6 z-50 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
      <div className="max-w-[420px] rounded-xl border border-gray-200 bg-white shadow-2xl p-3 text-xs text-gray-800">
        <div className="font-semibold text-red-700 mb-1 flex items-center gap-1">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500" />
          {category || "Issue"}
        </div>
        <div className="leading-snug whitespace-pre-wrap break-words">{message}</div>
      </div>
    </div>
  );

  // A single data cell with optional error/tooltip
  const FieldCell = ({ cell }) => {
    const hasIssue = !!cell?.issue;
    const issueText =
      typeof cell?.issue === "string" ? cell.issue : cell?.issue?.message || "";

    return (
      <td
        className={`group relative px-4 py-3 border-r border-gray-200 whitespace-nowrap align-top 
        ${hasIssue ? "bg-red-50/70" : "bg-white"} 
        hover:bg-orange-50 transition-colors`}
      >
        <span
          className={`block truncate max-w-[260px] ${
            hasIssue ? "text-gray-900" : "text-gray-800"
          }`}
        >
          {cell?.value ?? ""}
        </span>

        {hasIssue && (
          <>
            {/* red dot indicator */}
            <span className="absolute top-2 right-2 inline-block w-2 h-2 rounded-full bg-red-500 ring-2 ring-red-100" />
            {/* single custom tooltip */}
            <Tooltip category={cell?.category} message={issueText} />
          </>
        )}
      </td>
    );
  };

  /* ----------------------- Dedup Logic ----------------------- */

  // Normalize whitespace and casing for stable keys
  const norm = (s) =>
    (s || "")
      .toString()
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  // Deduplicate message objects by normalized (title + message)
  const dedupeMessages = (arr) => {
    const seen = new Set();
    const out = [];
    for (const m of arr) {
      const key = `${norm(m.title)}|${norm(m.message)}`;
      if (!seen.has(key)) {
        seen.add(key);
        out.push(m);
      }
    }
    return out;
  };

  /* ----------------------- Message Builders ----------------------- */

  // Build a messages array for a given row + category (with dedupe)
  const collectCategoryMessages = (row, category) => {
    // (1) Timeliness array on the row (preferred for "Timeliness")
    const fromTimeliness =
      category === "Timeliness" && Array.isArray(row.timeliness)
        ? row.timeliness.map((t) => ({
            title: t.scenario || "Timeliness Evaluation",
            message: t.message || "",
          }))
        : [];

    // (2) Row-level issues tagged with this category
    const rowLevel =
      (row.issues || [])
        .filter((i) => i.category === category)
        .map((i) => ({
          title: i.title || i.field || category,
          message: i.message || "",
        })) || [];

    // (3) Field-level issues tagged with this category
    const fieldLevel =
      Object.entries(row.data || {})
        .filter(([, cell]) => cell?.category === category && cell?.issue)
        .map(([field, cell]) => ({
          title: field,
          message:
            typeof cell.issue === "string"
              ? cell.issue
              : cell.issue?.message || "",
        })) || [];

    // Merge (Timeliness first if applicable), then DE-DUPE
    const merged =
      category === "Timeliness"
        ? [...fromTimeliness, ...rowLevel, ...fieldLevel]
        : [...rowLevel, ...fieldLevel];

    return dedupeMessages(merged);
  };

  /* ----------------------- Filters ----------------------- */

  const rowMatchesCategoryFilter = (row) => {
    if (!selectedCategories.length) return true;

    const hasFieldMatch = Object.values(row.data || {}).some((cell) =>
      selectedCategories.includes(cell?.category)
    );

    const hasRowMatch =
      (row.issues || []).some((i) => selectedCategories.includes(i.category)) ||
      (row.timeliness && selectedCategories.includes("Timeliness"));

    return hasFieldMatch || hasRowMatch;
  };

  const rowHasAnyIssue = (row) =>
    Object.values(row.data || {}).some((c) => !!c?.issue) ||
    (Array.isArray(row.issues) && row.issues.length > 0) ||
    (Array.isArray(row.timeliness) && row.timeliness.length > 0);

  /* ----------------------- Render ----------------------- */

  return (
    <>
      {/* Group header row */}
      <tr>
        <td
          colSpan={1 + selectedCategories.length + allFields.length}
          className="bg-gradient-to-r from-orange-50 to-orange-100 text-orange-900 text-sm px-4 py-2 font-semibold cursor-pointer 
          hover:from-orange-100 hover:to-orange-200 transition rounded-md border border-orange-200 shadow-sm"
          onClick={() => setExpanded((v) => !v)}
        >
          <span
            className="inline-flex items-center gap-2 select-none"
            aria-label={expanded ? "Collapse group" : "Expand group"}
          >
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-md border border-orange-300 bg-white text-orange-700 text-xs">
              {expanded ? "▼" : "▶"}
            </span>
            {groupLabel}
          </span>
        </td>
      </tr>

      {expanded &&
        rows.map((row, idx) => {
          // apply filters
          if (!rowMatchesCategoryFilter(row)) return null;
          if (onlyShowErroredRows && !rowHasAnyIssue(row)) return null;

          const displayRowIndex = row.rowIndex ?? row._row ?? row.index ?? idx + 1;

          return (
            <tr
              key={displayRowIndex}
              className={`border-b border-gray-200 transition-colors ${
                idx % 2 ? "bg-gray-50" : "bg-white"
              } hover:bg-orange-50/70`}
            >
              {/* sticky row number */}
              <td className="px-4 py-3 font-semibold text-center sticky left-0 z-10 bg-gray-100/90 text-gray-800 border-r border-gray-200 shadow-sm rounded-md backdrop-blur">
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-500" />
                  {displayRowIndex}
                </span>
              </td>

              {/* category columns (deduped messages) */}
              {selectedCategories.map((category) => {
                const messages = collectCategoryMessages(row, category);
                const hasMessages = messages.length > 0;

                return (
                  <td
                    key={`cat-${displayRowIndex}-${category}`}
                    className={`px-4 py-3 align-top border-r border-gray-200 ${
                      hasMessages ? "bg-yellow-50/70" : "bg-white text-gray-300"
                    }`}
                  >
                    {hasMessages ? (
                      <div className="space-y-2">
                        <div className="text-[11px] uppercase tracking-wide font-semibold text-yellow-900">
                          {category}
                        </div>
                        <div className="space-y-2 text-xs leading-snug">
                          {messages.map((m, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <span className="mt-1 inline-block w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                              <div className="min-w-0">
                                <div className="font-medium text-gray-900 truncate">
                                  {m.title}
                                </div>
                                <div className="text-gray-700 whitespace-pre-wrap break-words">
                                  {m.message}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs">—</span>
                    )}
                  </td>
                );
              })}

              {/* data cells */}
              {allFields.map((field) => (
                <FieldCell key={field} cell={row.data?.[field]} />
              ))}
            </tr>
          );
        })}
    </>
  );
}
